import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from "react";
import { initializeApp, getApps } from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as firebaseSignOut
} from "firebase/auth";
import { getFirestore, doc, onSnapshot } from "firebase/firestore";

import { claimTesterInvite } from "./api";

const INTERNAL_APP_NAME = "aclymate-internal";

const internalFirebaseConfig = {
  apiKey: process.env.REACT_APP_INTERNAL_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_INTERNAL_FIREBASE_AUTH_DOMAIN,
  projectId:
    process.env.REACT_APP_INTERNAL_FIREBASE_PROJECT_ID || "aclymate-internal",
  appId: process.env.REACT_APP_INTERNAL_FIREBASE_APP_ID,
  messagingSenderId:
    process.env.REACT_APP_INTERNAL_FIREBASE_MESSAGING_SENDER_ID,
  storageBucket: process.env.REACT_APP_INTERNAL_FIREBASE_STORAGE_BUCKET
};

const configIsComplete = (config) =>
  Boolean(config.apiKey && config.authDomain && config.projectId && config.appId);

const isTestEnvironment = () =>
  process.env.REACT_APP_ENVIRONMENT === "testing";

const getInternalApp = () => {
  if (!isTestEnvironment()) {
    return null;
  }
  const existing = getApps().find((app) => app.name === INTERNAL_APP_NAME);
  if (existing) {
    return existing;
  }
  if (!configIsComplete(internalFirebaseConfig)) {
    return null;
  }
  return initializeApp(internalFirebaseConfig, INTERNAL_APP_NAME);
};

const defaultContext = {
  isLoading: true,
  isConfigured: false,
  testerUser: null,
  tester: null,
  isTester: false,
  internalDb: null,
  signIn: async () => {},
  signOut: async () => {},
  getIdToken: async () => null
};

const TesterIdentityContext = createContext(defaultContext);

export const useTesterIdentity = () => useContext(TesterIdentityContext);

const TesterIdentityProvider = ({ children }) => {
  const internalApp = useMemo(() => getInternalApp(), []);
  const isConfigured = Boolean(internalApp);
  const internalAuth = useMemo(
    () => (internalApp ? getAuth(internalApp) : null),
    [internalApp]
  );
  const internalDb = useMemo(
    () => (internalApp ? getFirestore(internalApp) : null),
    [internalApp]
  );

  const [testerUser, setTesterUser] = useState(undefined);
  const [tester, setTester] = useState(null);
  const [testerDocLoading, setTesterDocLoading] = useState(false);
  const [claimedInviteForUid, setClaimedInviteForUid] = useState(null);

  useEffect(() => {
    if (!internalAuth) {
      setTesterUser(null);
      return undefined;
    }
    const unsubscribe = onAuthStateChanged(internalAuth, (user) => {
      setTesterUser(user || null);
    });
    return unsubscribe;
  }, [internalAuth]);

  useEffect(() => {
    if (!internalDb || !testerUser) {
      setTester(null);
      setTesterDocLoading(false);
      return undefined;
    }
    setTesterDocLoading(true);
    const ref = doc(internalDb, "testers", testerUser.uid);
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        setTester(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setTesterDocLoading(false);
      },
      (err) => {
        console.warn(
          "[tester-chat] tester doc subscription error:",
          err?.message || err
        );
        setTester(null);
        setTesterDocLoading(false);
      }
    );
    return unsubscribe;
  }, [internalDb, testerUser]);

  const signIn = useCallback(async () => {
    if (!internalAuth) {
      throw new Error("Aclymate-internal Firebase config missing");
    }
    const provider = new GoogleAuthProvider();
    await signInWithPopup(internalAuth, provider);
  }, [internalAuth]);

  const signOut = useCallback(async () => {
    if (!internalAuth) {
      return;
    }
    await firebaseSignOut(internalAuth);
  }, [internalAuth]);

  const getIdToken = useCallback(async () => {
    const currentUser = internalAuth?.currentUser;
    if (!currentUser) {
      return null;
    }
    return currentUser.getIdToken();
  }, [internalAuth]);

  useEffect(() => {
    if (!testerUser || tester || testerDocLoading) {
      return;
    }
    if (claimedInviteForUid === testerUser.uid) {
      return;
    }
    setClaimedInviteForUid(testerUser.uid);
    claimTesterInvite({ getIdToken }).catch((err) => {
      if (err?.status !== 404) {
        console.warn("[tester-chat] claimTesterInvite failed:", err.message);
      }
    });
  }, [testerUser, tester, testerDocLoading, claimedInviteForUid, getIdToken]);

  const isLoading =
    !isConfigured || testerUser === undefined || testerDocLoading;
  const isTester = Boolean(tester && tester.status === "active");

  const value = useMemo(
    () => ({
      isLoading,
      isConfigured,
      testerUser: testerUser || null,
      tester,
      isTester,
      internalDb,
      signIn,
      signOut,
      getIdToken
    }),
    [
      isLoading,
      isConfigured,
      testerUser,
      tester,
      isTester,
      internalDb,
      signIn,
      signOut,
      getIdToken
    ]
  );

  return (
    <TesterIdentityContext.Provider value={value}>
      {children}
    </TesterIdentityContext.Provider>
  );
};

export default TesterIdentityProvider;
